import {
  SHIPMENT_STOP_ROLE_VISUAL,
  shipmentStopRoleFromIndex,
} from '@gpx-spa/constants';
import type {
  V2ShipmentDetail,
  V2ShipmentStop,
} from '@gpx-spa/queries/src/getAccountShipmentV2';

export type ShipmentStopView = {
  id: string;
  label: string;
  address: string;
  type: 'origin' | 'waypoint' | 'destination';
  status: 'pending' | 'arrived' | 'departed' | 'delivered';
  plannedAt: string | null;
  actualAt: string | null;
};

export type ShipmentActivityView = {
  id: string;
  type: string;
  title: string;
  description: string;
  timestamp: string;
  actor: string;
};

const FAKE_STOPS: ShipmentStopView[] = [
  {
    id: 'fake-origin',
    label: 'Stop 1',
    address: '1200 Industrial Pkwy, Dallas, TX',
    type: 'origin',
    status: 'departed',
    plannedAt: '2026-06-01T08:00:00Z',
    actualAt: '2026-06-01T08:45:00Z',
  },
  {
    id: 'fake-waypoint',
    label: 'Stop 2',
    address: '450 Logistics Center Dr, Memphis, TN',
    type: 'waypoint',
    status: 'arrived',
    plannedAt: '2026-06-02T14:00:00Z',
    actualAt: '2026-06-02T15:10:00Z',
  },
  {
    id: 'fake-destination',
    label: 'Stop 3',
    address: '88 Distribution Way, Atlanta, GA',
    type: 'destination',
    status: 'pending',
    plannedAt: '2026-06-03T18:00:00Z',
    actualAt: null,
  },
];

const FAKE_ACTIVITY: ShipmentActivityView[] = [
  {
    id: 'act-1',
    type: 'shipment_created',
    title: 'Shipment created',
    description: 'Shipment was created and assigned to the logistics team.',
    timestamp: '2026-05-28T10:15:00Z',
    actor: 'System',
  },
  {
    id: 'act-2',
    type: 'status_change',
    title: 'Status changed to Active',
    description: 'Device reported in transit from origin.',
    timestamp: '2026-06-01T09:00:00Z',
    actor: 'System',
  },
  {
    id: 'act-3',
    type: 'exception_created',
    title: 'Route deviation detected',
    description: 'Device left the planned route corridor.',
    timestamp: '2026-06-02T11:30:00Z',
    actor: 'Monitoring',
  },
  {
    id: 'act-4',
    type: 'note_added',
    title: 'Note added',
    description: 'Driver confirmed delay at Memphis waypoint.',
    timestamp: '2026-06-02T16:00:00Z',
    actor: 'Ops Team',
  },
];

function stopTypeFromIndex(
  index: number,
  total: number,
): ShipmentStopView['type'] {
  const role = shipmentStopRoleFromIndex(index, total);
  if (role === 'origin') return 'origin';
  if (role === 'destination') return 'destination';
  return 'waypoint';
}

function mapStop(
  stop: V2ShipmentStop,
  index: number,
  total: number,
  shipment: V2ShipmentDetail,
): ShipmentStopView {
  const type = stopTypeFromIndex(index, total);
  let status: ShipmentStopView['status'] = 'pending';
  if (type === 'origin' && shipment.started_at) {
    status = 'departed';
  } else if (type === 'destination' && shipment.ended_at) {
    status = 'delivered';
  }

  return {
    id: String(stop.id ?? `${index}`),
    label: stop.name?.trim() || `Stop ${index + 1}`,
    address: stop.name?.trim() || '—',
    type,
    status,
    plannedAt: stop.planned_departure_at ?? stop.planned_arrival_at ?? null,
    actualAt:
      type === 'origin'
        ? (shipment.started_at ?? null)
        : type === 'destination'
          ? (shipment.ended_at ?? null)
          : null,
  };
}

export function stopsForShipment(
  shipment: V2ShipmentDetail | null | undefined,
): ShipmentStopView[] {
  const stops = shipment?.stops;
  if (stops?.length && shipment) {
    const sorted = [...stops].sort((a, b) => a.sequence - b.sequence);
    return sorted.map((stop, index) =>
      mapStop(stop, index, sorted.length, shipment),
    );
  }
  return FAKE_STOPS;
}

export function activityForShipment(
  _shipment: V2ShipmentDetail | null | undefined,
): ShipmentActivityView[] {
  return FAKE_ACTIVITY;
}

export function stopTypeBadgeClass(type: ShipmentStopView['type']): string {
  return SHIPMENT_STOP_ROLE_VISUAL[type].cardRoleBadgeClass;
}
